import { Button, Grid, Loader, SearchField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";
import * as Icons from "@mui/icons-material";

import {
  alertDevicesByDeviceId,
  deviceActionsByDeviceID,
  devicesByBranchID,
  devicesByCompanyID,
  getDevice,
  listActions,
  ruleDevicesByDeviceId,
} from "graphql/queries";
import {
  createDeviceActionLog,
  deleteAlertDevice,
  deleteDevice,
  deleteRuleDevice,
  sendDeviceMsg,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { DeviceCreateForm, DeviceUpdateForm } from ".";
import { getBranchName, getCompanyName } from "graphql/custom-queries";
import { LayoutContext } from "layout/context/LayoutContext";
import { valueTypes } from "config/constants";
import DevicesTable from "components/devices/DevicesTable";
import DeviceDetails from "components/devices/DeviceDetails";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import DeviceActionValues from "components/devices/DeviceActionValues";
import DeviceActionLogs from "components/devices/DeviceActionLogs";
import LoaderFullScreen from "components/utils/LoaderFullScreen";
import DeviceCalibration from "components/devices/DeviceCalibration";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const { ContentCopy } = Icons;

const Devices = ({ onSelectDevice }) => {
  const { me } = useContext(LayoutContext);
  const [
    canCreate,
    canUpdate,
    canDelete,
    canReadActionLogs,
    canExecuteActions,
    canReadCalibration,
  ] = usePermissions([
    "device:create",
    "device:update",
    "device:delete",
    "device:read-action-logs",
    "device:execute-actions",
    "device:read-calibration",
  ]);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [keywordState, setKeywordState] = useState("");
  const [deviceActions, setDeviceActions] = useState([]);
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const [deviceDetails, setDeviceDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    deviceId: null,
    deviceName: null,
  });
  const [modalDevice, setModalDevice] = useState({
    visible: false,
    deviceId: null,
  });
  const [modalActionLogs, setModalActionLogs] = useState({
    visible: false,
    deviceId: null,
    deviceName: null,
  });
  const [modalCalibration, setModalCalibration] = useState({
    visible: false,
    deviceId: null,
  });
  const [modalAction, setModalAction] = useState({
    visible: false,
    deviceId: null,
    action: null,
    currentValue: null,
  });
  const [loadingPage, setLoadingPage] = useState(false);
  const columnsMapped = useMemo(
    () =>
      devices.map((item) => {
        const currentActionsObj = JSON.parse(item.actionsObj || "{}");
        const actionsFiltered = deviceActions
          ?.find((devAct) => devAct.deviceID === item.id)
          ?.actions?.map((actItem) => {
            if (actItem.valueType === valueTypes[0]) {
              return (
                <ActionIcon
                  name={actItem.name}
                  icon={actItem.icon}
                  color={actItem.iconColor}
                  onClick={() =>
                    sendAction(
                      item.id,
                      actItem.id,
                      actItem.shortName,
                      actItem.successMsg,
                      actItem.errorMsg
                    )
                  }
                />
              );
            } else if (
              actItem.valueType === valueTypes[1] &&
              actItem.values?.length
            ) {
              const actionToRender = currentActionsObj[actItem.shortName]
                ? actItem.values.find(
                    (val) => val.value === currentActionsObj[actItem.shortName]
                  )
                : actItem.values[0];
              return (
                <ActionIcon
                  name={actItem.name}
                  icon={actionToRender.icon}
                  color={actionToRender.iconColor}
                  onClick={() =>
                    setModalAction({
                      visible: true,
                      deviceId: item.id,
                      action: actItem,
                      currentValue: actionToRender.value,
                    })
                  }
                />
              );
            } else if (actItem.valueType === valueTypes[2]) {
              return (
                <ActionIcon
                  name={actItem.name}
                  icon={actItem.icon}
                  color={actItem.iconColor}
                  onClick={() =>
                    setModalAction({
                      visible: true,
                      deviceId: item.id,
                      action: actItem,
                      currentValue: currentActionsObj[actItem.shortName],
                    })
                  }
                />
              );
            }
            return null;
          });

        return {
          ...item,
          id: (
            <>
              {item.id}{" "}
              <ContentCopy
                onClick={() => copyToClipboard(item.id)}
                style={styles.copyIcon}
                color="info"
              ></ContentCopy>
            </>
          ),
          companyName: item.companyName || <Loader />,
          branchName: item.branchName || <Loader />,
          _cellProps: {
            id: { style: { whiteSpace: "nowrap" } },
          },
          deviceActions:
            (actionsFiltered?.length > 0 && !onSelectDevice && (
              <>{actionsFiltered}</>
            )) ??
            [],
          actions: (
            <>
              <Button
                className="px-1"
                children={
                  deviceDetails?.id === item.id
                    ? strings.actions.hide
                    : strings.actions.show
                }
                variation="link"
                size="small"
                onClick={() => {
                  deviceDetails?.id === item.id
                    ? setDeviceDetails(null)
                    : fetchDevice(item.id);
                }}
              />
              {canUpdate && !onSelectDevice && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              {canDelete && !onSelectDevice && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      deviceId: item.id,
                      deviceName: item.name,
                    });
                  }}
                />
              )}
              {canReadActionLogs && !onSelectDevice && (
                <Button
                  className="px-1"
                  children={strings.actions.actionsLog}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalActionLogs({
                      visible: true,
                      deviceId: item.id,
                      deviceName: item.name,
                    });
                  }}
                />
              )}
              {canReadCalibration && !onSelectDevice && (
                <Button
                  className="px-1"
                  children={strings.actions.calibration}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalCalibration({
                      visible: true,
                      deviceId: item.id,
                    });
                  }}
                />
              )}
              {onSelectDevice && (
                <Button
                  className="px-1"
                  children={strings.actions.select}
                  variation="link"
                  size="small"
                  onClick={() => {
                    onSelectDevice({
                      deviceId: item.id,
                      deviceName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices, deviceDetails?.id, deviceActions]
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(strings.textCopied);
  };

  const onNext = () => {
    fetchDevices(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchDevices(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchDevices(undefined, undefined, keywordSplit);
    setKeywordState(keyword);
  };

  const openModal = (action, id) =>
    setModalDevice({ visible: true, action, deviceId: id });

  const onSuccessAction = () => {
    setModalDevice({ visible: false });
    resetFetchDevices();
  };

  const resetFetchDevices = () => {
    fetchDevices();
  };

  const sendAction = async (
    id,
    actionId,
    actionName,
    successRes,
    errorRes,
    saveData = false
  ) => {
    try {
      const actionSplitted = actionName.split(":");
      setLoadingPage(true);
      const res = await API.graphql(
        graphqlOperation(sendDeviceMsg, {
          input: {
            deviceId: id,
            action: actionName,
            saveData,
          },
        })
      );

      API.graphql({
        query: createDeviceActionLog.replaceAll("__typename", ""),
        variables: {
          input: {
            deviceID: id,
            actionID: actionId,
            userEmail: me.email,
            actionName: actionSplitted[0],
            actionValue: actionSplitted[1],
          },
        },
      });

      if (res.data.sendDeviceMsg?.statusCode === 200) {
        toast.success(successRes || strings.successDefault);
        setDevices((prevList) => {
          const tempList = [...prevList];

          const index = tempList.findIndex((dev) => dev.id === id);

          const newActionsObj = JSON.parse(tempList[index].actionsObj || "{}");
          newActionsObj[actionSplitted[0]] = actionSplitted[1];

          if (modalAction.action?.valueType === valueTypes[1])
            setModalAction((prev) => ({
              ...prev,
              currentValue: actionSplitted[1],
            }));

          tempList[index] = {
            ...tempList[index],
            actionsObj: JSON.stringify(newActionsObj),
          };

          return tempList;
        });
      } else toast.error(errorRes || strings.errorDefault);
      setLoadingPage(false);
    } catch (err) {
      toast.error(errorRes);
      setLoadingPage(false);
    }
  };

  async function fetchDeviceActions(deviceIds) {
    try {
      setDeviceActions([]);
      if (!canExecuteActions) return;
      if (!deviceIds?.length) return;

      const deviceActionsItems = [];
      await Promise.all(
        deviceIds.map(async (devId) => {
          const deviceActionsData = await API.graphql(
            graphqlOperation(deviceActionsByDeviceID, {
              deviceID: devId,
            })
          );
          deviceActionsItems.push(
            ...(deviceActionsData.data.deviceActionsByDeviceID.items ?? [])
          );
        }) ?? []
      );

      if (deviceActionsItems?.length) {
        const actionsData = await API.graphql(graphqlOperation(listActions));
        const actionsItems = actionsData.data.listActions.items ?? [];
        const deviceActionsUpdated = [];

        deviceActionsItems.forEach((devAct) => {
          const actionFound = actionsItems?.find(
            (act) => act.id === devAct.actionID
          );
          if (
            !deviceActionsUpdated.some(
              (newDevAct) => devAct.deviceID === newDevAct.deviceID
            )
          )
            deviceActionsUpdated.push({
              deviceID: devAct.deviceID,
              actions: [
                {
                  ...actionFound,
                  values: actionFound.values
                    ? JSON.parse(actionFound.values)
                    : [],
                },
              ],
            });
          else {
            const index = deviceActionsUpdated.findIndex(
              (newDevAct) => newDevAct.deviceID === devAct.deviceID
            );
            deviceActionsUpdated[index] = {
              ...deviceActionsUpdated[index],
              actions: [
                ...deviceActionsUpdated[index].actions,
                {
                  ...actionFound,
                  values: actionFound.values
                    ? JSON.parse(actionFound.values)
                    : [],
                },
              ],
            };
          }
        });

        setDeviceActions(deviceActionsUpdated);
      }
    } catch (err) {
      toast.error(strings.responses.errorFetchActions);
    }
  }

  const getNamesByDevices = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const branchName = await fetchBranchName(item.branchID);

      setDevices((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
          branchName,
        };

        return updatedList;
      });
    });
  };

  async function fetchDevices(token, isNext = true, keywords) {
    try {
      setDevicesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      let objOperation = {
        companyID: searchBy.company.id || me.companyID,
      };
      let nameOperation = devicesByCompanyID;
      let nameOperationStr = "devicesByCompanyID";

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
        };
        nameOperation = devicesByBranchID;
        nameOperationStr = "devicesByBranchID";
      }

      const limitMapped = !onSelectDevice ? 10 : 3;
      const devicesData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      id: { contains: key },
                    },
                    {
                      name: { contains: key },
                    },
                    {
                      location: { contains: key },
                    },
                    {
                      brand: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const devicesItems = devicesData.data[nameOperationStr].items;
      setDevices(devicesItems);
      getNamesByDevices(devicesItems);
      fetchDeviceActions(devicesItems?.map((dev) => dev.id));
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: devicesData.data[nameOperationStr].nextToken,
        };
      });
      setDevicesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setDevicesLoading(false);
    } finally {
      setDevicesLoading(false);
    }
  }

  async function fetchDevice(id) {
    try {
      const deviceData = await API.graphql(
        graphqlOperation(getDevice, {
          id,
        })
      );
      const deviceItem = deviceData.data.getDevice;
      setDeviceDetails(deviceItem);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }
  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }
  async function fetchBranchName(id) {
    try {
      const branchData = await API.graphql(
        graphqlOperation(getBranchName, {
          id,
        })
      );
      const branchItem = branchData.data.getBranch;
      return branchItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchBranchName", err);
      return "";
    }
  }
  async function deviceDelete(id) {
    try {
      await alertsDeviceDelete(id);
      await rulesDeviceDelete(id);

      const result = await API.graphql(
        graphqlOperation(deleteDevice, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteDevice.id;
      setDevices(devices.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        deviceId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  async function alertsDeviceDelete(deviceId) {
    try {
      const results = await API.graphql(
        graphqlOperation(alertDevicesByDeviceId, { deviceId })
      );
      const alertsDeviceToDelete = results.data.alertDevicesByDeviceId.items;
      await Promise.all(
        alertsDeviceToDelete?.map((alertDevice) =>
          API.graphql(
            graphqlOperation(deleteAlertDevice, {
              input: { id: alertDevice.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  async function rulesDeviceDelete(deviceId) {
    try {
      const results = await API.graphql(
        graphqlOperation(ruleDevicesByDeviceId, { deviceId })
      );
      const rulesDeviceToDelete = results.data.ruleDevicesByDeviceId.items;
      await Promise.all(
        rulesDeviceToDelete?.map((ruleDevice) =>
          API.graphql(
            graphqlOperation(deleteRuleDevice, {
              input: { id: ruleDevice.id },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "id",
      label: strings.table.headers.id,
      _style: { width: "20%" },
    },
    {
      key: "companyName",
      label: strings.table.headers.company,
      _style: { width: "15%" },
    },
    {
      key: "branchName",
      label: strings.table.headers.branch,
      _style: { width: "15%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "deviceActions",
      label: strings.table.headers.deviceActions,
      _style: { width: "15%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "15%" },
    },
  ];

  return (
    <div>
      {onSelectDevice ? (
        <span className="fs-4">{strings.titleAvailable}</span>
      ) : (
        <span className="fs-3">{strings.title}</span>
      )}

      {canCreate && !onSelectDevice && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addDevice}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter searchBy={searchBy} setSearchBy={setSearchBy} />
          <SearchField
            label={strings.table.search.label}
            value={keywordState}
            hasSearchButton={false}
            hasSearchIcon={true}
            placeholder={strings.table.search.placeholder}
            onChange={(event) => onKeywordSubmit(event.target.value)}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {devicesLoading ? (
          <Loader />
        ) : (
          <DevicesTable
            columns={tableColumns}
            items={columnsMapped}
            pagination={paginationTokens}
            onNext={onNext}
            onPrevious={onPrevious}
          />
        )}
        <DeviceDetails device={deviceDetails} />
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.deviceName,
        })}
        onCancel={() => setModalDetails({ visible: false, deviceId: null })}
        onConfirm={() => deviceDelete(modalDetails.deviceId)}
      />
      {modalDevice.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalDevice.visible}
          onClose={() => setModalDevice({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalDevice.action === "create"
                ? strings.modalDevice.addTitle
                : strings.modalDevice.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            {modalDevice.action === "create" ? (
              <DeviceCreateForm onSuccess={onSuccessAction} />
            ) : (
              <DeviceUpdateForm
                deviceId={modalDevice.deviceId}
                onSuccess={onSuccessAction}
              />
            )}
          </CModalBody>
        </CModal>
      )}
      {modalAction.deviceId && (
        <DeviceActionValues
          visible={modalAction.visible}
          deviceId={modalAction.deviceId}
          action={modalAction.action}
          currentValue={modalAction.currentValue}
          onClose={() => setModalAction({ visible: false })}
          sendAction={sendAction}
        />
      )}
      {modalActionLogs.visible && (
        <DeviceActionLogs
          deviceID={modalActionLogs.deviceId}
          deviceName={modalActionLogs.deviceName}
          visible={modalActionLogs.visible}
          onClose={() => setModalActionLogs({ visible: false })}
        />
      )}
      {modalCalibration.visible && (
        <DeviceCalibration
          deviceID={modalCalibration.deviceId}
          visible={modalCalibration.visible}
          onClose={() => setModalCalibration({ visible: false })}
        />
      )}
      {loadingPage && <LoaderFullScreen />}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Dispositivos",
    titleAvailable: "Dispositivos Disponibles",
    successDeleted: "Dispositivo eliminado con éxito",
    actions: {
      addDevice: "Agregar Dispositivo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      select: "Seleccionar",
      actionsLog: "Historial de acciones",
      calibration: "Calibración",
    },
    responses: {
      errorFetch: "Error al consultar dispositivos",
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchBranches: "Error al consultar sedes",
      errorSendAction: "Error al realizar la acción",
    },
    table: {
      headers: {
        id: "ID",
        company: "Empresa",
        branch: "Sede",
        location: "Ubicación",
        name: "Nombre",
        brand: "Marca",
        deviceActions: "Acciones",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    fields: {
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el dispositivo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalDevice: {
      addTitle: "Agregar Dispositivo",
      updateTitle: "Actualizar Dispositivo",
    },
    successDeviceOn: "El dispositivo fue encendido con éxito",
    successDeviceOff: "El dispositivo fue apagado con éxito",
    textCopied: "Texto copiado",
    successDefault: "Acción enviada con éxito!",
    errorDefault: "Ha ocurrido un error al enviar la acción",
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
  iconAction: {
    cursor: "pointer",
    fontSize: "30px",
  },
  iconActionNone: {
    cursor: "pointer",
  },
  copyIcon: {
    cursor: "pointer",
    fontSize: "18px",
  },
  action: {
    alignSelf: "center",
    padding: "0px 5px",
  },
};

export default Devices;
